import React from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import { MobileExpandableDetailCard, MobileStatusChip } from '../../components/mobile'
import { MOBILE_SECTION_DIVIDER_COLOR } from '../../components/mobile/styles/mobileColors'
import { getDateOnly } from '../../helpers/formatDate'
import { formatMoney } from '../../helpers/utils'
import { CHARGE_STATUS_OPTIONS, DOCUMENT_TYPE_OPTIONS } from '../constants/chargeStatus'

const MOBILE_CARD_DIVIDER_COLOR = MOBILE_SECTION_DIVIDER_COLOR
const MOBILE_CARD_ACCENT = '#597FA9'

const getFormattedDate = (date) => getDateOnly(date) || 'N/A'

const formatNullableMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  return formatMoney(value)
}

const buildChargeStatusUI = (status) => {
  const statusUI = CHARGE_STATUS_OPTIONS[status]

  if (statusUI) return statusUI

  return {
    label: String(status || '-').toUpperCase(),
    className: ''
  }
}

const buildChargeSummaryRows = (model) => [
  {
    label: 'Fecha',
    value: getFormattedDate(model.header_created_date),
    valueTitle: getFormattedDate(model.header_created_date)
  },
  {
    label: 'Cliente',
    value: `${model.header_client_uid || '-'} - ${model.header_client_name || '-'}`,
    valueTitle: `${model.header_client_uid || '-'} - ${model.header_client_name || '-'}`
  },
  {
    label: 'Pagado',
    value: formatNullableMoney(model.document_amount),
    valueTitle: formatNullableMoney(model.document_amount),
    boldValue: true
  }
]

const buildChargeDetailRows = (model) => [
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-user`,
    icon: <AccountCircleOutlinedIcon sx={{ fontSize: 20 }} />,
    label: 'Usuario',
    value: `${model.header_user_uid || '-'} - ${model.header_user_full_name || '-'}`,
    valueTitle: `${model.header_user_uid || '-'} - ${model.header_user_full_name || '-'}`,
    valueSx: {
      display: 'block',
      wordBreak: 'break-word'
    },
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  },
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-document`,
    icon: <ArticleOutlinedIcon sx={{ fontSize: 20 }} />,
    label: '# de documento',
    value: model.document_reference || 'N/A',
    valueTitle: model.document_reference || undefined,
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  },
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-document-type`,
    icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />,
    label: 'Tipo de cobro',
    value: DOCUMENT_TYPE_OPTIONS[model.document_type] || model.document_type || '-',
    valueTitle: DOCUMENT_TYPE_OPTIONS[model.document_type] || model.document_type || undefined,
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  },
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-due-date`,
    icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />,
    label: 'Fecha de vencimiento',
    value:
      model.document_type === 'DR' || model.document_type === 'P'
        ? 'N/A'
        : getFormattedDate(model.document_due_date),
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  },
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-total`,
    icon: <PaymentsOutlinedIcon sx={{ fontSize: 20 }} />,
    label: 'Total a cobrar',
    value: formatNullableMoney(model.document_total),
    boldValue: true,
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  },
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-discount`,
    icon: <LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />,
    label: 'Descuento',
    value: formatNullableMoney(model.early_payment_discount_amount),
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  },
  {
    detailRowKey: `${model.header_db_ref || model.document_reference}-balance`,
    icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />,
    label: 'Saldo',
    value: formatNullableMoney(model.pending_balance),
    boldValue: true,
    dividerColor: MOBILE_CARD_DIVIDER_COLOR
  }
]

const buildChargeFooter = (model) => {
  const statusUI = buildChargeStatusUI(model.header_status)

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        component="span"
        sx={{
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.04
        }}
      >
        Estado:
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <MobileStatusChip
          statusUI={statusUI}
          sx={{
            minWidth: 112,
            '& .MuiChip-label': {
              whiteSpace: 'normal',
              textAlign: 'center'
            }
          }}
        />
      </Box>
    </Stack>
  )
}

const ChargeMobileCard = ({ model, openChargeInNewTab, expanded, onToggleExpand }) => {
  const code = model.header_db_ref || model.document_reference || 'Cobro'
  const canOpenCharge = Boolean(model.header_db_ref)

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 0, display: 'flex', alignItems: 'stretch' }}>
      <MobileExpandableDetailCard
        code={code}
        onCodeClick={canOpenCharge ? () => openChargeInNewTab(model.header_db_ref) : undefined}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        summaryRows={buildChargeSummaryRows(model)}
        detailRows={buildChargeDetailRows(model)}
        accentColor={MOBILE_CARD_ACCENT}
        codeAccentColor={MOBILE_CARD_ACCENT}
        detailDividerColor={MOBILE_CARD_DIVIDER_COLOR}
        footerContent={buildChargeFooter(model)}
        footerDivider
        footerDividerColor={MOBILE_CARD_DIVIDER_COLOR}
        footerPaddingTop={1.5}
        expandAriaLabel={expanded ? 'Ocultar detalle del cobro' : 'Mostrar detalle del cobro'}
        detailRegionAriaLabel={`Detalle del cobro ${code}`}
        bodyFontSize={14}
        codeFontSize={14}
        summaryLabelFontSize={14}
        summaryValueFontSize={14}
        detailLabelFontSize={14}
        detailValueFontSize={14}
      />
    </Grid>
  )
}

export default ChargeMobileCard
